// ─────────────────────────────────────────────────────────────────────────────
// MovementProfileLoader
//
// Lê e VALIDA os arquivos JSON de comportamento ao iniciar o jogo / carregar o
// mapa. Falha cedo e com mensagem clara — um typo num waypoint não deve virar
// um NaN silencioso no meio do jogo.
// ─────────────────────────────────────────────────────────────────────────────

import {
  AmbientBirdsFile,
  BirdSpawnerConfig,
  NpcBehaviorFile,
  NpcProfile,
} from './schema';

/** Remove recursivamente chaves de comentário (`//...`, `_...`) do JSON cru. */
export function stripComments<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(stripComments) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k.startsWith('//') || k.startsWith('_')) continue;
      out[k] = stripComments(v);
    }
    return out as T;
  }
  return value;
}

class ValidationError extends Error {
  constructor(msg: string) {
    super(`[MovementProfileLoader] ${msg}`);
    this.name = 'ValidationError';
  }
}

function num(v: unknown, path: string): number {
  if (typeof v !== 'number' || Number.isNaN(v)) {
    throw new ValidationError(`${path}: esperado número, recebi ${JSON.stringify(v)}`);
  }
  return v;
}

function validateNpcProfile(id: string, p: NpcProfile): void {
  const at = `profiles.${id}`;
  if (p.type === 'static') return;
  if (p.type !== 'patrol') {
    throw new ValidationError(`${at}.type inválido: ${JSON.stringify((p as { type: unknown }).type)}`);
  }
  num(p.speed, `${at}.speed`);
  if (!Array.isArray(p.waypoints) || p.waypoints.length < 1) {
    throw new ValidationError(`${at}.waypoints: precisa de pelo menos 1 ponto`);
  }
  p.waypoints.forEach((w, i) => {
    num(w.x, `${at}.waypoints[${i}].x`);
    num(w.y, `${at}.waypoints[${i}].y`);
    if (w.wait != null) num(w.wait, `${at}.waypoints[${i}].wait`);
    if (w.action != null && !p.animation?.actions?.[w.action]) {
      throw new ValidationError(
        `${at}.waypoints[${i}].action "${w.action}" não existe em animation.actions`,
      );
    }
  });
  if (!p.animation?.sheet) {
    throw new ValidationError(`${at}.animation.sheet é obrigatório em perfis 'patrol'`);
  }
  num(p.animation.cols, `${at}.animation.cols`);
  num(p.animation.rows, `${at}.animation.rows`);
  const start = p.startIndex ?? 0;
  if (start < 0 || start >= p.waypoints.length) {
    throw new ValidationError(`${at}.startIndex ${start} fora do intervalo 0..${p.waypoints.length - 1}`);
  }
}

function validateBirdSpawner(id: string, s: BirdSpawnerConfig): void {
  const at = `spawners.${id}`;
  num(s.maxAlive, `${at}.maxAlive`);
  num(s.spawnIntervalMin, `${at}.spawnIntervalMin`);
  num(s.spawnIntervalMax, `${at}.spawnIntervalMax`);
  if (s.spawnIntervalMax < s.spawnIntervalMin) {
    throw new ValidationError(`${at}: spawnIntervalMax < spawnIntervalMin`);
  }
  if (!Array.isArray(s.edges) || s.edges.length === 0) {
    throw new ValidationError(`${at}.edges: precisa de pelo menos 1 borda`);
  }
  num(s.flight?.speedMinPxPerSec, `${at}.flight.speedMinPxPerSec`);
  num(s.flight?.speedMaxPxPerSec, `${at}.flight.speedMaxPxPerSec`);
  num(s.flight?.altitude?.bobPeriodSec, `${at}.flight.altitude.bobPeriodSec`);
  num(s.flight?.curve?.swayPeriodSec, `${at}.flight.curve.swayPeriodSec`);
}

export class MovementProfileLoader {
  private npc: NpcBehaviorFile | null = null;
  private birds: AmbientBirdsFile | null = null;

  /**
   * Carrega os dois arquivos em paralelo. Os caminhos são relativos à raiz do
   * site (pasta `public/`), então funcionam sem rebuild ao editar o JSON.
   */
  async load(
    npcUrl = '/data/movement/npc_behavior.json',
    birdsUrl = '/data/movement/ambient_birds.json',
  ): Promise<void> {
    const [npcRaw, birdRaw] = await Promise.all([
      this.fetchJson(npcUrl),
      this.fetchJson(birdsUrl),
    ]);

    const npc = stripComments(npcRaw) as NpcBehaviorFile;
    const birds = stripComments(birdRaw) as AmbientBirdsFile;

    if (!npc.profiles || typeof npc.profiles !== 'object') {
      throw new ValidationError(`${npcUrl}: campo "profiles" ausente`);
    }
    for (const [id, p] of Object.entries(npc.profiles)) validateNpcProfile(id, p);

    if (!birds.spawners || typeof birds.spawners !== 'object') {
      throw new ValidationError(`${birdsUrl}: campo "spawners" ausente`);
    }
    for (const [id, s] of Object.entries(birds.spawners)) validateBirdSpawner(id, s);

    this.npc = npc;
    this.birds = birds;
  }

  private async fetchJson(url: string): Promise<unknown> {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) {
      throw new ValidationError(`falha ao buscar ${url} — HTTP ${res.status}`);
    }
    try {
      return await res.json();
    } catch (e) {
      throw new ValidationError(`${url}: JSON inválido — ${(e as Error).message}`);
    }
  }

  getNpcProfile(id: string): NpcProfile {
    const p = this.npc?.profiles[id];
    if (!p) throw new ValidationError(`perfil de NPC "${id}" não existe`);
    return p;
  }

  getBirdSpawner(id: string): BirdSpawnerConfig {
    const s = this.birds?.spawners[id];
    if (!s) throw new ValidationError(`spawner de pássaros "${id}" não existe`);
    return s;
  }

  listNpcProfiles(): string[] {
    return this.npc ? Object.keys(this.npc.profiles) : [];
  }

  listBirdSpawners(): string[] {
    return this.birds ? Object.keys(this.birds.spawners) : [];
  }
}
